import React, { useCallback, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cs from 'classnames';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';

import { getPayments } from '../../redux/modules/paymentsList';
import {
  ContractId,
  Amount,
  PayMethod,
  MakePayment,
  DownloadPdf,
} from './CustomCells';
import DataTable from '../DataTable/DataTable';
import CustomTableFilters from '../TableFilters/CustomTableFilters';
import { tableDateFormat } from '../../helpers/dates';
import styles from './PaymentsScreen.module.scss';
import { FILE_FORMATS } from '../../constants';
import { exportRecurringPayments } from '../../api/paymentApi';
import { showNotification } from "../../helpers/notificationHelpers";

const LE = '(le)';
const GE = '(ge)';

const getUTF16File = (data, format) => {
  let charCode, byteArray = [];
  byteArray.push(255, 254);

  for (let i = 0; i < data.length; ++i) {
    charCode = data.charCodeAt(i);
    byteArray.push(charCode & 0xff);
    byteArray.push(charCode / 256 >>> 0);
  }

  return new Blob([new Uint8Array(byteArray)], {type:`text/${format};charset=UTF-16LE;`});
}

const cols = [
  {
    title: 'paymentTbl.fName',
    property: 'firstName',
    sortable: true,
    filter: { type: 'string' },
  },
  {
    title: 'paymentTbl.lName',
    property: 'lastName',
    sortable: true,
    filter: { type: 'string' },
  },
  {
    title: 'paymentTbl.email',
    property: 'email',
    sortable: true,
    isForAdminOnly: true,
    filter: { type: 'string' },
  },
  {
    title: 'paymentTbl.mobile_phone',
    property: 'mobilePhone',
    sortable: true,
    isForAdminOnly: true,
    filter: { type: 'string' },
  },
  {
    title: 'paymentTbl.amount',
    property: 'amount',
    sortable: true,
    customCell: (row, cell, prps, getSrt) => Amount(row, cell, prps, getSrt),
    filter: { type: 'string' },
  },
  {
    title: 'paymentTbl.payMethod.title',
    property: 'paymentMethodId',
    sortable: true,
    customCell: (row, cell, prps, getSrt) => PayMethod(row, cell, prps, getSrt),
    filter: {
      type: 'dropdown',
      options: [
        { value: '1', label: 'paymentTbl.payMethod.card' },
        { value: '2', label: 'paymentTbl.payMethod.idram' },
      ],
    },
  },
  {
    title: 'paymentTbl.date',
    property: 'createdAt',
    sortable: true,
    format: tableDateFormat,
    filter: {
      type: 'date',
      queryParams: [
        {
          searchParamKey: 'created_at[]',
          operator: GE,
        },
        {
          searchParamKey: 'created_at[]',
          operator: LE,
        },
      ],
    },
  },
];

const PaymentsScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const { isAdmin, isAccountant } = useSelector(
    (state) => state.userReducer.user,
    shallowEqual,
  );

  const { payments, isLoading } = useSelector(
    (store) => store.paymentsReducer,
    shallowEqual,
  );

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [filters, setFilters] = useState({});

  const handleActBtnClick = (row) => {
    history.push({
      pathname: '/contracts/recurring-payment',
      state: {
        policyNumber: row.contractId,
      },
    });
  };

  const columns = [
    {
      title: 'paymentTbl.contractId',
      property: 'contractId',
      sortable: true,
      customCell: (r, c, p, getSrt) => ContractId(r, c, p, getSrt, handleActBtnClick),
      filter: { type: 'string' },
    },
    ...cols.filter(({ isForAdminOnly }) => isAdmin || !isForAdminOnly),
    {
      title: '',
      sortable: false,
      customCell: (row, cell) => DownloadPdf(row, cell),
    },
    {
      title: '',
      sortable: false,
      customCell: (row) => MakePayment(row, handleActBtnClick),
    },
  ];

  const toggleFiltersBlock = () => {
    setIsCollapsed(!isCollapsed);
  };

  const loadData = useCallback(
    (params) => {
      dispatch(getPayments({ params, filters }));
    },
    [dispatch, filters],
  );

  const exportData = useCallback(
    async (params) => {
      const { fileFormat } = params;
      if (Object.values(FILE_FORMATS).includes(fileFormat)) {
        if(fileFormat === FILE_FORMATS.XML && !filters.paymentMethodId) {
          showNotification(
            'errorNotifications.paymentExportXML',
            'error',
            'EXPORT_PAYMENTS_ERROR',
          );
          return;
        }
        const data = await exportRecurringPayments(params, filters);
        saveAs(getUTF16File(data, fileFormat), `payments.${fileFormat}`);
      }
    },
    [filters],
  );

  return (
    <div id={styles.paymentsContainer}>
      <div className={cs(styles.paymentsHeader, styles.mobileContent)}>
        {t('headerTitles.myPayments')}
      </div>

      <div className={cs(styles.payments_title_row, 'headerRow')}>
        <div className={cs(styles.payments_title, 'header')}>
          {t('profilePage.payments')}
        </div>
        <div>
          {isAccountant && (
            <>
              <button
                type="button"
                className={cs(styles.export_icon, styles.export_csv_icon)}
                onClick={() => exportData({ fileFormat: FILE_FORMATS.CSV })}
              />
              <button
                type="button"
                className={cs(styles.export_icon, styles.export_xml_icon)}
                onClick={() => exportData({ fileFormat: FILE_FORMATS.XML })}
              />
            </>
          )}
          <button
            label="icon"
            type="button"
            className={cs({
              [styles.filter_icon]: true,
              [styles.expanded]: !isCollapsed,
              white_button: true,
            })}
            onClick={toggleFiltersBlock}
          />
        </div>
      </div>

      {!isCollapsed && (
        <CustomTableFilters
          filters={filters}
          setFilters={setFilters}
          columns={columns}
          itemsPerRow={isAdmin ? 5 : 4}
        />
      )}

      <DataTable
        data={payments.data}
        totalCount={payments.total}
        columns={columns}
        onParamsChanges={loadData}
        loading={isLoading}
      />
    </div>
  );
};

export default PaymentsScreen;
